import { DateRangeFieldValue } from "@tellescope/forms";
import { useTwilioBillingQuery } from "@tellescope/tanstack-query";
import { ButtonGroup, DateRangeField, Skeleton, useStateWithSetAction } from "@tellescope/ui";
import { Spinner, Select } from "@tellescope/design-system";
import { formatCurrency } from "@tellescope/utils";
import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { endOfDay } from "date-fns/endOfDay";
import { endOfMonth } from "date-fns/endOfMonth";
import { startOfDay } from "date-fns/startOfDay";
import { startOfMonth } from "date-fns/startOfMonth";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const VALID_PREDEFINED_RANGES = [
  "current",
  "previous",
  "days:7",
  "days:14",
  "days:30",
  "months:3",
  "months:12",
  "custom",
] as const;

/**
 * Stub component while we wait for backend support for showing a full, complex usage chart
 */
export const TwilioUsageChart = () => {
  const { t } = useTranslation("settings");
  const [dateRange, setDateRange] = useState<DateRangeFieldValue>({
    from: startOfMonth(new Date()).getTime(),
    to: endOfDay(new Date()).getTime(),
  });
  const [selectedPredefinedRange, setSelectedPredefinedRange] = useStateWithSetAction<
    (typeof VALID_PREDEFINED_RANGES)[number] | undefined
  >("current", (newRange) => {
    if (newRange === "custom" || newRange === undefined) return;
    switch (newRange) {
      case "current":
        setDateRange({
          from: startOfMonth(new Date()).getTime(),
          to: endOfDay(new Date()).getTime(),
        });
        return;
      case "previous": {
        const lastMonth = addMonths(new Date(), -1);
        setDateRange({
          from: startOfMonth(lastMonth).getTime(),
          to: endOfMonth(lastMonth).getTime(),
        });
        return;
      }
      case "days:7":
      case "days:14":
      case "days:30":
      case "months:3":
      case "months:12": {
        const [unitStr, countStr] = newRange.split(":");
        if (!unitStr || !countStr) return;
        const count = Number(countStr);
        if (Number.isNaN(count)) return;
        setDateRange({
          from: startOfDay(
            unitStr === "days" ? addDays(new Date(), -1 * count) : addMonths(new Date(), -1 * count)
          ).getTime(),
          to: endOfDay(new Date()).getTime(),
        });
        return;
      }
    }
  });

  const query = useTwilioBillingQuery({
    request: {
      from: new Date(dateRange.from ?? "").toISOString(),
      to: new Date(dateRange.to ?? "").toISOString(),
    },
    enabled: !!dateRange.from && !!dateRange.to,
  });

  return (
    <section className="p-6 space-y-6">
      <div className="flex justify-between w-full">
        <span className="flex gap-0">
          <ButtonGroup>
            <Select
              value={selectedPredefinedRange === "custom" ? undefined : selectedPredefinedRange}
              onValueChange={(newRange) =>
                setSelectedPredefinedRange(VALID_PREDEFINED_RANGES.find((item) => item === newRange))
              }
            >
              <Select.Trigger>
                <Select.Value placeholder={t("Select period")} />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="current">{t("Current Billing Cycle")}</Select.Item>
                <Select.Item value="previous">{t("Previous Billing Cycle")}</Select.Item>
                <Select.Item value="days:7">{t("Last 7 days")}</Select.Item>
                <Select.Item value="days:14">{t("Last 14 days")}</Select.Item>
                <Select.Item value="days:30">{t("Last 30 days")}</Select.Item>
                <Select.Item value="months:3">{t("Last 3 months")}</Select.Item>
                <Select.Item value="months:12">{t("Last 12 months")}</Select.Item>
              </Select.Content>
            </Select>
            <DateRangeField
              value={dateRange}
              onChange={(newRange) => {
                setSelectedPredefinedRange(undefined);
                setDateRange(newRange);
              }}
              clearable={false}
              className="rounded-l-none"
            />
          </ButtonGroup>
        </span>
      </div>
      <div className="border rounded-md p-6">
        <h3 className="text-2xl font-semibold flex items-center gap-2">
          {t("Total Spend: ")}
          {query.isLoading || !query.data?.data?.total ? (
            <>
              <Skeleton className="w-40 h-8" />
              <Spinner size="xs" />
            </>
          ) : (
            formatCurrency(query.data?.data.total, "USD")
          )}
        </h3>
      </div>
    </section>
  );
};
